// Phase 8.b.7 — manager-only per-investor drilldown route.
// Renders the position dict returned by GET /ops/funds/{id}/investors/{investor_id}
// (the same shape an investor sees on /portfolio/fund, plus
// unpaid_mgmt_fee_base + nav_history). Reachable from the INVESTORS table
// on /fund via the investor's row.
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { PageHeader, Stat } from '../../components/shell';
import { NavSparkline } from '../../components/NavSparkline';
import { fmtBase, fmtUnitPriceOps } from '../../lib/format';

const fmtUnits = (s: string | number) =>
  (typeof s === 'string' ? Number(s) : s).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
const fmtDT = (iso: string) => new Date(iso).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

export default function FundInvestorDetail() {
  const { fundId, investorId } = useParams<{ fundId: string; investorId: string }>();
  const [pos, setPos] = useState<any | null | 'error'>(null);

  const load = useCallback(() => {
    if (!fundId || !investorId) return;
    api<any>(`/ops/funds/${fundId}/investors/${investorId}`)
      .then(setPos)
      .catch(() => setPos('error'));
  }, [fundId, investorId]);

  useEffect(() => { load(); }, [load]);

  if (pos === null) return <div className="view container"><p className="label-s">Loading…</p></div>;
  if (pos === 'error') return (
    <div className="view container">
      <p className="serif-body">Unable to load investor detail. The investor may not have any units in this fund.</p>
      <Link to="/fund" className="label-s">← BACK TO FUND</Link>
    </div>
  );

  const isUSD = (pos.base_currency || 'USD') === 'USD';
  // RULE-FEE-WINDUP fires only on the last unitholder out, so an unconditional
  // column would be a wall of zeroes on every ordinary statement of activity.
  // Rendered only when a row actually carries one — the same rule the statement
  // PDF applies.
  const showWindupFee = (pos.redemptions || []).some(
    (r: any) => Number(r.final_mgmt_fee_base ?? r.final_mgmt_fee_usd ?? 0) !== 0);
  // OB.3 — fund hasn't struck a NAV covering this investor yet; value/NAV/P&L
  // come back null. Show "pending" rather than a misleading $0 / −100%.
  const pending = pos.valuation_pending === true;
  const pnl = pending ? 0 : Number(isUSD ? pos.unrealized_pnl_usd : pos.unrealized_pnl_base);
  return (
    <div className="view container">
      <PageHeader
        kicker={`${(pos.fund_name || 'FUND').toUpperCase()} · INVESTOR DETAIL`}
        title={<><em>{pos.investor_name || 'Investor'}.</em></>}
        meta={<span className="label-s">AS OF {fmtDT(pos.as_of)}</span>}
      />
      <Link to="/fund" className="label-s" style={{ marginTop: 12, display: 'inline-block' }}>← BACK TO FUND</Link>
      {/* 16.e.2 — edit the investor's identity in place. Issued statements keep */}
      {/* the name they were issued under (snapshotted at close, 16.e.2a).        */}
      <InvestorEditCard
        investorId={investorId!}
        name={pos.investor_name || ''}
        legalName={pos.investor_legal_name || ''}
        entityType={pos.investor_entity_type || 'llc'}
        onSaved={load}
      />
      {pending && (
        <p className="serif-body" style={{ marginTop: 16, fontSize: 15, color: 'var(--ink-2)', maxWidth: '60ch' }}>
          Valuation pending. This investor's subscription is recorded; their units
          will be valued at the fund's next valuation.
        </p>
      )}
      <div className="agg-strip" style={{ ['--cols' as any]: 4, marginTop: 24 } as any}>
        <div className="agg-cell"><Stat label="UNITS HELD" value={fmtUnits(pos.units)} /></div>
        <div className="agg-cell"><Stat label="CURRENT VALUE" value={pending ? '—' : fmtBase(Number(isUSD ? pos.value_usd : pos.value_base), pos.base_currency)} sub={`CONTRIBUTED ${fmtBase(Number(isUSD ? pos.contributed_usd : pos.contributed_base), pos.base_currency)}`} /></div>
        <div className="agg-cell"><Stat label="UNREALIZED P&L" value={pending ? '—' : <span className={pnl >= 0 ? 'pos' : 'neg'}>{fmtBase(pnl, pos.base_currency)}</span>} sub={pending ? 'AWAITING FIRST VALUATION' : `AVG COST ${fmtUnitPriceOps(Number(isUSD ? pos.avg_cost_per_unit : pos.avg_cost_per_unit_base), pos.base_currency)}/UNIT`} /></div>
        <div className="agg-cell"><Stat label="UNPAID MGMT FEE" value={fmtBase(Number(pos.unpaid_mgmt_fee_base || 0), pos.base_currency)} /></div>
      </div>

      {/* 16.d.5 — NAV/unit history (fund-public series; the same chart the */}
      {/* investor sees on their own /fund view). Units-only fund: the drilldown */}
      {/* shows the unit price over time, not per-investor positions.          */}
      {Array.isArray(pos.nav_history) && pos.nav_history.length >= 2 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">NAV PER UNIT · HISTORY</span></div>
          <NavSparkline series={pos.nav_history.map((h: any) => ({ x: h.struck_at, y: Number(isUSD ? h.nav_per_unit : h.nav_per_unit_base) }))} currency={pos.base_currency || 'USD'} />
        </section>
      )}

      {/* Phase 9.b LOW 15 — fund terms display for the manager drilldown */}
      {/* so ops can review the fee schedule alongside the investor's     */}
      {/* position without flipping back to the Settings panel.            */}
      {pos.terms && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">FUND TERMS</span></div>
          <div className="agg-strip" style={{ ['--cols' as any]: 4, marginTop: 12 } as any}>
            <div className="agg-cell"><Stat label="MGMT FEE" value={`${pos.terms.management_fee_annual_pct}% / YEAR`} /></div>
            <div className="agg-cell"><Stat label="PERF FEE" value={`${pos.terms.performance_fee_pct}%`} sub={pos.terms.hurdle_kind === 'none' ? 'NO HURDLE' : `${pos.terms.hurdle_annual_pct}% HURDLE`} /></div>
            <div className="agg-cell"><Stat label="CRYSTALLIZES" value={String(pos.terms.crystallization_cadence || '').toUpperCase()} /></div>
            <div className="agg-cell"><Stat label="DEALING" value={String(pos.terms.dealing_basis || '').toUpperCase()} sub={pos.terms.dealing_tz} /></div>
          </div>
        </section>
      )}

      {/* 16.d.3.4b — grant the investor an OPTIONAL read-only portal login. */}
      <PortalAccessCard
        fundId={fundId!} investorId={investorId!}
        portalAccess={pos.portal_access} portalEmail={pos.portal_user_email}
        onInvited={load}
      />

      {Array.isArray(pos.subscriptions) && pos.subscriptions.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <div className="kicker-row"><span className="kicker">SUBSCRIPTIONS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>EFFECTIVE (UTC)</th><th data-align="right">AMOUNT</th><th data-align="right">NAV / UNIT</th><th data-align="right">UNITS</th></tr></thead>
            <tbody>
              {pos.subscriptions.map((s: any, i: number) => (
                <tr key={i}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(s.effective_at)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(isUSD ? s.amount_usd : s.amount_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtUnitPriceOps(Number(isUSD ? s.nav_per_unit : s.nav_per_unit_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtUnits(s.units_issued)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {Array.isArray(pos.redemptions) && pos.redemptions.length > 0 && (
        <section style={{ marginTop: 32 }}>
          <div className="kicker-row"><span className="kicker">REDEMPTIONS</span></div>
          <table className="tbl" style={{ marginTop: 12 }}>
            <thead><tr><th>EFFECTIVE (UTC)</th><th data-align="right">UNITS</th><th data-align="right">NAV / UNIT</th><th data-align="right">PAID</th><th data-align="right">REDEMPTION FEE</th>{showWindupFee && <th data-align="right">MGMT FEE WITHHELD</th>}</tr></thead>
            <tbody>
              {pos.redemptions.map((r: any, i: number) => (
                <tr key={i}>
                  <td className="num" style={{ fontSize: 12 }}>{fmtDT(r.effective_at)}</td>
                  <td data-align="right" className="num">{fmtUnits(r.units_redeemed)}</td>
                  <td data-align="right" className="num">{fmtUnitPriceOps(Number(isUSD ? r.nav_per_unit : r.nav_per_unit_base), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.amount_usd_paid : r.amount_base_paid), pos.base_currency)}</td>
                  <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.redemption_fee_usd : r.redemption_fee_base), pos.base_currency)}</td>
                  {showWindupFee && <td data-align="right" className="num">{fmtBase(Number(isUSD ? r.final_mgmt_fee_usd : r.final_mgmt_fee_base), pos.base_currency)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

// 16.d.3.4b — PORTAL ACCESS: show the investor's read-only login status, or an
// opt-in invite form. POSTs /ops/funds/{id}/investors/{id}/portal-invite; on
// success refetches the detail (onInvited) so the status flips to active.
function PortalAccessCard({ fundId, investorId, portalAccess, portalEmail, onInvited }: {
  fundId: string; investorId: string; portalAccess?: string; portalEmail?: string | null;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (portalAccess === 'active') {
    return (
      <section style={{ marginTop: 40 }}>
        <div className="kicker-row"><span className="kicker">PORTAL ACCESS</span></div>
        <p className="serif-body" style={{ marginTop: 10, color: 'var(--ink-2)' }}>
          This investor has a read-only portal login: <span className="num">{portalEmail}</span>.
        </p>
      </section>
    );
  }
  return (
    <section style={{ marginTop: 40 }}>
      <div className="kicker-row"><span className="kicker">PORTAL ACCESS</span></div>
      <p className="serif-body" style={{ marginTop: 10, color: 'var(--ink-2)', maxWidth: '60ch' }}>
        Invite this investor to a read-only portal login — they can view their unit position and
        statements. Optional; leave it if they're reported out of band.
      </p>
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input
          className="field-input" type="email" placeholder="investor@example.com"
          value={email} onChange={e => { setEmail(e.target.value); setErr(null); }}
          style={{ maxWidth: 280 }}
        />
        <button
          className="btn" disabled={busy || !email.trim()}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              await api(`/ops/funds/${fundId}/investors/${investorId}/portal-invite`,
                        { method: 'POST', body: { email: email.trim() } });
              onInvited();
            } catch (e: any) {
              setErr(e?.payload?.detail || e?.message || 'Invite failed.');
            } finally { setBusy(false); }
          }}
        >Invite to portal <span className="btn-arrow">→</span></button>
      </div>
      {err && <p className="label-s" style={{ color: 'var(--oxblood)', marginTop: 8 }}>{err}</p>}
    </section>
  );
}

// 16.e.2 — edit the investor's identity fields (display name / legal name /
// entity type) in place via PATCH /ops/investors/{id}. A name edit never restates
// an already-issued statement: the name is snapshotted into the statement at
// close (16.e.2a). On save, refetch the drilldown so the title updates.
function InvestorEditCard({ investorId, name, legalName, entityType, onSaved }: {
  investorId: string; name: string; legalName: string; entityType: string; onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [dn, setDn] = useState(name);
  const [ln, setLn] = useState(legalName);
  const [et, setEt] = useState(entityType || 'llc');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-seed the fields whenever the parent reloads with fresh values.
  useEffect(() => { setDn(name); setLn(legalName); setEt(entityType || 'llc'); }, [name, legalName, entityType]);

  const save = async () => {
    if (busy) return;
    if (!dn.trim()) { setErr('Give the investor a name.'); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/ops/investors/${investorId}`, { method: 'PATCH', body: {
        display_name: dn.trim(), legal_name: ln.trim(), entity_type: et,
      } });
      setOpen(false); onSaved();
    } catch (e: any) {
      setErr(e?.payload?.detail || 'Could not save the investor. Check the fields and try again.');
    } finally { setBusy(false); }
  };

  if (!open) return (
    <div>
      <button type="button" className="btn-link" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
        Edit details
      </button>
    </div>
  );

  return (
    <section style={{ marginTop: 28, maxWidth: '46ch' }}>
      <div className="kicker-row"><span className="kicker">EDIT DETAILS</span></div>
      <div className="field" style={{ marginTop: 12 }}>
        <label className="field-label">DISPLAY NAME</label>
        <input className="field-input" value={dn} onChange={e => { setDn(e.target.value); setErr(null); }} />
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <label className="field-label">LEGAL NAME</label>
        <input className="field-input" value={ln} onChange={e => setLn(e.target.value)} />
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <label className="field-label">ENTITY TYPE</label>
        <select className="field-input" value={et} onChange={e => setEt(e.target.value)}>
          <option value="llc">LLC</option>
          <option value="trust">Trust</option>
          <option value="individual">Individual</option>
          <option value="other">Other</option>
        </select>
      </div>
      {err && <p className="serif-body" style={{ fontStyle: 'italic', color: 'var(--oxblood)', fontSize: 14, marginTop: 12 }}>{err}</p>}
      <div style={{ display: 'flex', gap: 24, alignItems: 'center', marginTop: 16 }}>
        <button type="button" className="btn" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn-link" onClick={() => { setOpen(false); setErr(null); }}>Cancel</button>
      </div>
    </section>
  );
}
